/**
 * Flag-source display mapping — queue rows are labels rows, so the source
 * is `created_by`: `dreamer` (nightly dream scan), `eval:<slug>` (failing
 * eval case), anything else is a user email.
 */

import type { ReviewQueueItem } from '@/api/generated/model'

/**
 * Sidebar filter chips — the dreamer subtypes only. Eval/user flags are
 * rare enough that filtering by them isn't worth the chip space; their
 * rows still classify (and get hidden by an active type filter).
 */
export const FLAG_TYPES = ['model issue', 'prompt issue', 'good usage'] as const

// 'dismissed' is chip-less like eval/user flags: agent dismissals get their
// own bucket so they never pollute 'model issue', and (like eval flags) an
// active type filter hides them — the status row's DISMISSED view is where
// they live.
export type FlagType =
  | (typeof FLAG_TYPES)[number]
  | 'eval flag'
  | 'user flag'
  | 'dismissed'

/** Sidebar lean filter — 'all' means no verdict filtering. */
export type LeanFilter = 'all' | 'good' | 'bad'

/**
 * Sidebar review-state filter — single-select, its own chip row. 'all' is
 * the working view: everything EXCEPT dismissed (dismissed flags don't
 * belong in the eval at all, TRA-908). 'unreviewed' is the to-do view:
 * only rows still waiting for a human verdict (TRA-1067). 'reviewed' and
 * 'dismissed' are the read-back views of each settled state.
 */
export const STATUS_FILTERS = ['all', 'unreviewed', 'reviewed', 'dismissed'] as const
export type StatusFilter = (typeof STATUS_FILTERS)[number]

/** Whether a row survives the review-state filter. */
export function filterStatus(item: ReviewQueueItem, status: StatusFilter): boolean {
  if (status === 'all') return item.review_status !== 'dismissed'
  return item.review_status === status
}

/**
 * Model-filter sentinels (TRA-907). Radix Select forbids an empty value,
 * and neither of these can collide with a real slug: the gateway's model
 * ids are `team/slug`, so anything with a `__` and no slash is ours.
 */
export const ANY_MODEL = '__all__'
export const UNKNOWN_MODEL = '__unknown__'

/**
 * Trace key → model slug, from GET /api/review/queue/models. Models are
 * fetched separately from the queue (they cost one R2 body read per trace)
 * so the sidebar paints before they arrive; until then this is empty and
 * every row is model-unknown, which keeps the dropdown hidden.
 */
export type QueueModelIndex = Record<string, string | null>

/**
 * The models a queue can be split by, for the sidebar dropdown.
 *
 * Rows whose trace body couldn't be read carry no model; they get an
 * explicit `unknown` option rather than being silently unreachable
 * whenever a real model is selected.
 */
export function queueModels(
  items: readonly ReviewQueueItem[],
  modelByKey: QueueModelIndex,
): string[] {
  const named = new Set<string>()
  let anyUnknown = false
  for (const item of items) {
    const model = modelByKey[item.trace_key]
    if (model == null || model === '') anyUnknown = true
    else named.add(model)
  }
  const models = [...named].sort((a, b) => a.localeCompare(b))
  if (anyUnknown) models.push(UNKNOWN_MODEL)
  return models
}

/** Whether a row survives the model filter. */
export function filterModel(
  item: ReviewQueueItem,
  model: string,
  modelByKey: QueueModelIndex,
): boolean {
  if (model === ANY_MODEL) return true
  const rowModel = modelByKey[item.trace_key]
  if (model === UNKNOWN_MODEL) return rowModel == null || rowModel === ''
  return rowModel === model
}

/**
 * Display form of a model id: the gateway records the team-prefixed slug
 * (`cartage/cartage-1`), but inside one team's queue the prefix is noise.
 * Filter values stay canonical — this is labels only.
 */
export function displayModelName(model: string): string {
  if (model === UNKNOWN_MODEL) return 'unknown'
  return model.slice(model.lastIndexOf('/') + 1)
}

/**
 * The parts of a flag the verdict helpers below read.
 *
 * Both flag-carrying shapes satisfy it structurally: the review queue's
 * `ReviewQueueItem` and the trajectories listing's `RunFlag`. `notes` is
 * the flag text with the dream scan's marker still on the front — the
 * queue's shorter `summary` starts with the same prefix, so either one
 * classifies identically.
 */
export interface FlagLike {
  // 'held' (TRA-1057) is in the API union so generated rows stay
  // assignable, but held rows never reach the queue response — the
  // triage stage parks them server-side until a night with budget room.
  review_status: 'unreviewed' | 'reviewed' | 'dismissed' | 'held'
  approved: boolean
  notes: string
  created_by: string
}

export interface RowVerdict {
  lean: 'good' | 'bad'
  /** Who made the call — the reviewer's saved verdict, or the judge's lean. */
  source: 'reviewer' | 'agent'
}

/**
 * The verdict of record on a queue row, or null when nobody has made a call.
 *
 * A reviewed row's `approved` IS the verdict (TRA-915) and outranks the
 * judge's: the composer strips the `[good]`/`[bad]` marker before saving,
 * so a reviewed row's notes carry the reviewer's prose and nothing else —
 * without reading `approved` the row would chip nothing at all.
 *
 * Everywhere else the judge's lean is the only one there is: only the dream
 * scan writes a marker, and eval/user flags carry none. A dismissal makes no
 * claim about the run (the composer forces `approved` false), so dismissed
 * rows keep the flag's verdict — or none, which is the honest answer.
 */
export function rowVerdict(item: FlagLike): RowVerdict | null {
  if (item.review_status === 'reviewed') {
    return { lean: item.approved ? 'good' : 'bad', source: 'reviewer' }
  }
  const lean = parseVerdictSummary(item.notes).lean
  return lean === null ? null : { lean, source: 'agent' }
}

/**
 * Which lean filter a row falls under. Same verdict the row chips, except
 * that a filter has to bucket every row: a flag with no verdict IS a
 * failure report, so it counts as bad rather than vanishing from both.
 */
export function rowLean(item: FlagLike): 'good' | 'bad' {
  return rowVerdict(item)?.lean ?? 'bad'
}

/**
 * Which kind of flag a queue row is — the taxonomy `flagHeading` speaks
 * in sentence form. A settled row still names a type (not its status) so
 * the sidebar type filter classifies reviewed rows too; its verdict half
 * comes from `rowLean`, so a row a human marked good reads as good usage.
 */
export function flagType(item: FlagLike): FlagType {
  if (item.created_by === 'dreamer') {
    const verdict = parseVerdictSummary(item.notes)
    // An agent dismissal is not a model failure — without its own bucket
    // it would file under 'model issue' via rowLean's bad default.
    if (verdict.agentDismissed) return 'dismissed'
    if (rowLean(item) === 'good') return 'good usage'
    if (/^prompt issue\b/i.test(verdict.text)) {
      return 'prompt issue'
    }
    return 'model issue'
  }
  if (item.created_by.startsWith('eval:')) return 'eval flag'
  return 'user flag'
}

/**
 * Compact source + type label for queue rows and the header meta line.
 *
 * "agent flag" alone hides what KIND of thing the dreamer found, so
 * dreamer rows name the type. A terminal status replaces the type
 * entirely — once a row is settled, HOW it settled is the useful fact.
 */
export function flagSourceLabel(item: FlagLike): string {
  if (item.review_status === 'dismissed') return 'dismissed'
  if (item.review_status === 'reviewed') return 'reviewed'
  return flagType(item)
}

/**
 * Popover heading for the flag pin — a sentence, not a raw created_by.
 *
 * Dreamer flags name what kind of issue: the judge is instructed to begin
 * prompt-rooted reasons with "Prompt issue:", so that prefix (not a
 * guess) picks the heading; everything else it flags is model behavior.
 * Good-lean entries aren't flags at all — the agent just marked the run
 * as good usage.
 */
export function flagHeading(createdBy: string, notes: string): string {
  if (createdBy === 'dreamer') {
    const verdict = parseVerdictSummary(notes)
    if (verdict.agentDismissed) return 'Agent dismissed this trajectory'
    if (verdict.lean === 'good') return 'Agent marked this as good usage'
    if (/^prompt issue\b/i.test(verdict.text)) return 'Agent flagged a prompt issue'
    return 'Agent flagged a model issue'
  }
  if (createdBy.startsWith('eval:')) {
    return `Eval ${createdBy.slice('eval:'.length)} flagged an issue`
  }
  return `Flagged by ${createdBy}`
}

export interface VerdictSummary {
  /** null when the summary carries no verdict prefix (user-written notes). */
  lean: 'good' | 'bad' | null
  /** The judge dismissed this trajectory itself (`[dismissed]` marker) —
   * only the dream scan writes it, so it distinguishes an agent dismissal
   * from a human one. Makes no good/bad claim. */
  agentDismissed: boolean
  text: string
}

/**
 * Split the dream scan's `[bad] reason` / `[good] reason` / `[dismissed]
 * reason` note format into verdict + clean text. The prefix is OUR
 * formatting of the judge's structured `lean` field (dream_scan.py), not
 * model prose — so it's safe to parse back out and render as a chip
 * instead of leaking the brackets. Python twin: `parse_flag_notes` in
 * services/review_queue.py.
 */
export function parseVerdictSummary(summary: string): VerdictSummary {
  const match = /^\[(good|bad|dismissed)\]\s*/.exec(summary)
  if (match === null) return { lean: null, agentDismissed: false, text: summary }
  const text = summary.slice(match[0].length)
  if (match[1] === 'dismissed') return { lean: null, agentDismissed: true, text }
  return { lean: match[1] === 'good' ? 'good' : 'bad', agentDismissed: false, text }
}
